/**
 * Token Gating Hook
 * Thin wrapper around TokenGatingContext for backward compatibility
 * All API calls are now deduplicated and cached at the context level
 */

import { useCallback } from 'react';
import { getAxiosInstance } from 'services/api';
import { useAuth } from 'store/auth/hooks';
import { useWallets } from '@privy-io/react-auth';
import { useTokenGatingContext } from 'contexts/TokenGatingContext';

const api = getAxiosInstance();

// Re-export types from context
export type { TokenGatedFeature, TokenGatingStatus, WordLimitStatus } from 'contexts/TokenGatingContext';

export const useTokenGating = () => {
  const { user } = useAuth();
  const { wallets } = useWallets();

  // Use context for all token gating data - this prevents duplicate API calls
  const {
    status,
    wordLimitStatus,
    loading,
    error,
    fetchTokenStatus,
    fetchWordLimitStatus,
    refreshBalance,
    hasFeatureAccess,
    getFeatureDetails,
  } = useTokenGatingContext();

  // Get the primary wallet (embedded or connected)
  const primaryWallet = wallets.find(w => w.connectorType === 'embedded') || wallets[0];

  // Check specific feature access (direct API call for specific feature checks)
  const checkFeatureAccess = useCallback(async (feature: string): Promise<boolean> => {
    if (!user?._id) return false;

    try {
      const response = await api.get(`/token-gating/feature/${feature}`);
      return response.data.hasAccess;
    } catch (err: any) {
      console.error(`Error checking ${feature} access:`, err);
      return false;
    }
  }, [user]);

  // Get current word limit
  const getWordLimit = useCallback((): number => {
    return wordLimitStatus?.wordLimit || 100;
  }, [wordLimitStatus]);

  // Check if user has extended word limit
  const hasExtendedWordLimit = useCallback((): boolean => {
    return wordLimitStatus?.hasExtendedAccess || false;
  }, [wordLimitStatus]);

  // Check streaming access
  const hasStreamingAccess = useCallback((): boolean => {
    return hasFeatureAccess('STREAMING');
  }, [hasFeatureAccess]);

  // Check game server access
  const hasGameServerAccess = useCallback((): boolean => {
    // Game server access is independent of wallet and token-gating status.
    return true;
  }, []);

  // Check if wallet is properly connected via Privy
  const isPrivyWalletConnected = useCallback((): boolean => {
    return !!(user?._id && primaryWallet?.address && user?.accountAddress);
  }, [user, primaryWallet]);

  return {
    // Status
    status,
    wordLimitStatus,
    loading,
    error,

    // Methods
    fetchTokenStatus,
    fetchWordLimitStatus,
    checkFeatureAccess,
    refreshBalance,
    hasFeatureAccess,
    getFeatureDetails,
    getWordLimit,
    hasExtendedWordLimit,
    hasStreamingAccess,
    hasGameServerAccess,

    // Quick access
    isWalletConnected: status?.walletConnected || false,
    isPrivyWalletConnected: isPrivyWalletConnected(),
    tokenBalance: status?.tokenBalance || 0,
    wordLimit: wordLimitStatus?.wordLimit || 100,
    hasExtendedWords: wordLimitStatus?.hasExtendedAccess || false,
    primaryWallet,
  };
};